import { ENDPOINTS, getApiUrl } from "@/common";
import { KeyTokenName } from "@/constants";

let currentAudio: HTMLAudioElement | null = null;
let abortController: AbortController | null = null;
let currentRequestId = 0; // Định danh duy nhất cho mỗi lượt phát

export const stopAllAudio = () => {
  // Tăng ID để vô hiệu hóa các callback của lượt phát cũ ngay lập tức
  currentRequestId++;

  if (abortController) {
    abortController.abort();
    abortController = null;
  }

  if (currentAudio) {
    currentAudio.pause();
    currentAudio.removeAttribute("src");
    try {
      currentAudio.load();
    } catch (_) {}
    currentAudio = null;
  }

  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

let lastPlayedStr = "";
let lastPlayTimestamp = 0;

export const KEY_TTS_VOICE_GENDER = "tts_voice_gender";
export type TTSVoiceGender = "female" | "male";

export const getTTSVoiceGender = (): TTSVoiceGender => {
  if (typeof window === "undefined") return "female";
  const stored = localStorage.getItem(KEY_TTS_VOICE_GENDER);
  return stored === "male" ? "male" : "female";
};

export const setTTSVoiceGender = (gender: TTSVoiceGender): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY_TTS_VOICE_GENDER, gender);
  window.dispatchEvent(new CustomEvent("tts_voice_gender_changed", { detail: { gender } }));
};

export const stripEmojis = (text: string): string => {
  if (!text) return "";
  return text
    .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Ưu tiên phát TTS qua Backend Proxy (Edge TTS Neural / Google fallback).
 * Chỉ sử dụng Web Speech API (Native) làm phương án dự phòng khi API lỗi.
 */
export const onPlayReadStr = (
  str: string,
  onNext?: () => void,
  _lang: string = "vi-VN",
  speed?: number,
  gender?: TTSVoiceGender
) => {
  if (typeof window === "undefined" || !str) return;

  const cleanStr = stripEmojis(str);
  if (!cleanStr) return;

  const currentGender = gender || getTTSVoiceGender();
  const now = Date.now();
  // Ngăn chặn gọi trùng lặp cùng một câu trong vòng 600ms (debouncing)
  if (cleanStr === lastPlayedStr && now - lastPlayTimestamp < 600) {
    return;
  }
  lastPlayedStr = cleanStr;
  lastPlayTimestamp = now;

  stopAllAudio(); // Dừng lượt cũ và tăng requestId

  // Lưu lại ID của lượt phát HIỆN TẠI
  const requestId = currentRequestId;

  const storedSpeed = typeof window !== "undefined" ? localStorage.getItem("speed_audio") : null;
  const parsedStored = storedSpeed ? parseFloat(storedSpeed) : null;
  const rate = speed !== undefined ? speed : parsedStored && !isNaN(parsedStored) ? parsedStored : 1.18;

  // Hàm kiểm tra xem lượt phát này còn hiệu lực không
  const isActive = () => requestId === currentRequestId;

  const fallbackNative = () => {
    // Nếu ID đã thay đổi (có lượt hover mới), tuyệt đối không chạy Native của lượt cũ
    if (!isActive()) return;

    const synth = window.speechSynthesis;
    if (!synth) {
      onNext?.();
      return;
    }

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(cleanStr);
    utterance.lang = _lang;
    utterance.rate = rate;

    const voices = synth.getVoices();
    let voice = voices.find(
      (v) =>
        (v.lang === _lang || v.lang.replace("_", "-") === _lang || v.lang.startsWith(_lang.split("-")[0])) &&
        (currentGender === "male" ? /male|nam/i.test(v.name) : /female|nữ|hoaimy|linh/i.test(v.name))
    );
    if (!voice) {
      voice =
        voices.find((v) => v.lang === _lang || v.lang.replace("_", "-") === _lang) ||
        voices.find((v) => v.lang.startsWith(_lang.split("-")[0]));
    }

    if (voice) utterance.voice = voice;

    utterance.onend = () => {
      if (isActive()) onNext?.();
    };

    utterance.onerror = () => {
      if (isActive()) onNext?.();
    };

    synth.speak(utterance);
  };

  const apiLang = _lang.startsWith("vi") ? "vi" : "en";
  const ttsUrl = getApiUrl(ENDPOINTS.ITEMS_TTS, {
    text: cleanStr,
    lang: apiLang,
    gender: currentGender,
    speed: rate,
  });

  const audio = new Audio();
  audio.preload = "auto";
  currentAudio = audio;
  // Giữ playbackRate = 1 vì Backend EdgeTTS đã trực tiếp synthesize tốc độ tự nhiên (+18%) với chất lượng chuẩn
  audio.playbackRate = 1;

  audio.onended = () => {
    currentAudio = null;
    if (isActive()) onNext?.();
  };

  audio.onerror = () => {
    currentAudio = null;
    if (isActive()) fallbackNative();
  };

  audio.src = ttsUrl;
  const playPromise = audio.play();
  if (playPromise !== undefined) {
    playPromise.catch((e) => {
      if (isActive() && e.name !== "AbortError") {
        fallbackNative();
      }
    });
  }
};

export const onPlayGoogleTTS = (
  str: string,
  onNext?: () => void,
  lang = "vi-VN",
  speed?: number,
  gender?: TTSVoiceGender
) => {
  onPlayReadStr(str, onNext, lang, speed, gender);
};

/**
 * Pre-cache/Pre-warm background TTS audio so subsequent clicks/hovers play immediately (0ms latency).
 */
export const prewarmTTS = (
  texts: (string | undefined | null)[],
  lang = "vi",
  gender?: TTSVoiceGender,
  speed?: number
) => {
  if (typeof window === "undefined" || !texts || texts.length === 0) return;
  const currentGender = gender || getTTSVoiceGender();
  const apiLang = lang.startsWith("vi") ? "vi" : "en";
  const storedSpeed = localStorage.getItem("speed_audio");
  const parsedStored = storedSpeed ? parseFloat(storedSpeed) : null;
  const effectiveSpeed = speed !== undefined ? speed : parsedStored && !isNaN(parsedStored) ? parsedStored : 1.18;

  texts.filter(Boolean).forEach((txt) => {
    const text = stripEmojis((txt || "").trim());
    if (!text) return;
    const ttsUrl = getApiUrl(ENDPOINTS.ITEMS_TTS, {
      text,
      lang: apiLang,
      gender: currentGender,
      speed: effectiveSpeed,
    });
    try {
      // Background low-priority fetch to generate server-side cache
      fetch(ttsUrl, { priority: "low" as any }).catch(() => {});
    } catch (_) {}
  });
};

export interface IShadowingLoopOptions {
  text: string;
  speed?: number;
  lang?: string;
  pauseMs?: number;
  maxLoops?: number;
  onWordChange?: (wordIndex: number) => void;
  onLoopChange?: (loopNum: number) => void;
  onStop?: () => void;
}

export interface IShadowingLoopController {
  stop: () => void;
  isActive: () => boolean;
}

/**
 * Reusable helper for continuous shadowing repetition loop with word boundary tracking
 */
export const startShadowingLoop = (options: IShadowingLoopOptions): IShadowingLoopController => {
  let isRunning = true;
  let timer: any = null;

  const text = (options.text || "").trim();
  const speed = options.speed !== undefined ? options.speed : 1.0;
  const lang = options.lang || "en-US";
  const pauseMs = options.pauseMs !== undefined ? options.pauseMs : 1000;
  const maxLoops = options.maxLoops !== undefined ? options.maxLoops : 0;

  const targetTokens = text.split(/\s+/).filter(Boolean);
  const wordRanges: { start: number; end: number }[] = [];
  let cursor = 0;
  for (let i = 0; i < targetTokens.length; i++) {
    const tok = targetTokens[i];
    const foundStart = text.indexOf(tok, cursor);
    const start = foundStart !== -1 ? foundStart : cursor;
    const end = start + tok.length;
    wordRanges.push({ start, end });
    cursor = end;
  }

  const stop = () => {
    isRunning = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    options.onWordChange?.(-1);
    options.onStop?.();
  };

  const playIteration = (loopNum: number) => {
    if (!isRunning || typeof window === "undefined" || !window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = speed;

    const voices = window.speechSynthesis.getVoices();
    const enVoice =
      voices.find(
        (v) =>
          v.lang.startsWith("en") &&
          (v.name.includes("Samantha") ||
            v.name.includes("Alex") ||
            v.name.includes("Karen") ||
            v.name.includes("Enhanced"))
      ) ||
      voices.find((v) => v.lang.startsWith("en") && v.localService === true) ||
      voices.find((v) => v.lang === "en-US" || v.lang.startsWith("en"));
    if (enVoice) utterance.voice = enVoice;

    options.onLoopChange?.(loopNum);
    options.onWordChange?.(0);

    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      if (!isRunning || event.name !== "word") return;
      const charIdx = event.charIndex;
      for (let i = 0; i < wordRanges.length; i++) {
        const range = wordRanges[i];
        if (charIdx >= range.start - 2 && (i === wordRanges.length - 1 || charIdx < wordRanges[i + 1].start)) {
          options.onWordChange?.(i);
          break;
        }
      }
    };

    utterance.onend = () => {
      if (!isRunning) return;
      if (targetTokens.length > 0) {
        options.onWordChange?.(targetTokens.length - 1);
      }

      if (maxLoops > 0 && loopNum >= maxLoops) {
        stop();
        return;
      }

      const nextLoop = loopNum + 1;
      timer = setTimeout(() => {
        if (isRunning) {
          options.onWordChange?.(0);
          playIteration(nextLoop);
        }
      }, pauseMs);
    };

    utterance.onerror = () => {
      stop();
    };

    window.speechSynthesis.speak(utterance);
  };

  playIteration(1);

  return {
    stop,
    isActive: () => isRunning,
  };
};
